/* eslint-disable react-refresh/only-export-components --
 * `monogramFor` and `monogramTint` are pure helpers the tests exercise
 * directly, co-located with the component that is their only consumer.
 * Fast Refresh granularity does not apply to a file this small. */
import { TOKENS } from './ui/dashboard-primitives'

/**
 * Six tints drawn from the page gradient's own family (FR-D11).
 *
 * NOT a generated hue. Runbuds uses a purple gradient and Tolan a generated
 * colourful field; either would put a foreign colour on the coach screen and
 * break the single-accent discipline. Every one of these still looks like it
 * belongs to this app.
 */
const TINTS = ['#8c6653', '#554a5a', '#6d5647', '#4a4b55', '#7a5b52', '#5c5240'] as const

export function monogramTint(userId: number): string {
  return TINTS[Math.abs(userId) % TINTS.length]
}

/** First grapheme of each name. No ASCII fold: Ö stays Ö. */
export function monogramFor(first?: string | null, last?: string | null): string {
  const grapheme = (s?: string | null) => (s?.trim() ? [...s.trim()][0].toUpperCase() : '')
  return `${grapheme(first)}${grapheme(last)}`
}

/**
 * The 1px inner hairline every avatar carries, so a tinted circle still
 * reads as an object on the page gradient rather than a flat wash. Same
 * value as CoachAvatar.tsx's ring -- one avatar treatment across the app.
 */
const CREAM_HAIRLINE = TOKENS.creamBorder

interface MonogramProps {
  first?: string | null
  last?: string | null
  userId: number
  /** Diameter in px. Runtime-controlled, so this goes through inline style,
   * never a Tailwind class -- JIT cannot scan a template-literal class. */
  size: number
}

/**
 * The default identity, not a fallback (D10): a circle in the owner's tint
 * with their initials. An account that never uploads a photo should still
 * look intentional.
 */
export function Monogram({ first, last, userId, size }: MonogramProps) {
  const initials = monogramFor(first, last)
  return (
    <div
      aria-hidden="true"
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        backgroundColor: monogramTint(userId),
        boxShadow: `inset 0 0 0 1px ${CREAM_HAIRLINE}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <span
        style={{
          color: TOKENS.cream,
          fontWeight: 300,
          fontSize: size * 0.38,
          lineHeight: 1,
          // Glyph boxes are not optically centred -- nudge up 1.5% of the
          // diameter so the initials sit in the visual middle of the circle.
          transform: `translateY(${size * -0.015}px)`,
        }}
      >
        {initials}
      </span>
    </div>
  )
}
